/**
 * Settings entry "Show setup checklist" (tracker item 75), ADMIN only: shown
 * while the checklist is hidden, puts it back on the dashboard.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ISetupApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManageSetup } from '../utils/permissions';
import { getErrorMessage } from '../utils/apiError';
import { Toast } from './Toast';

export function SetupChecklistRestore() {
  const { user } = useAuth();
  const allowed = canManageSetup(user);
  const api = useMemo<ISetupApi>(() => createAPIClient(), []);
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  // Set after a restore so the entry can say where the checklist went
  const [restored, setRestored] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    api
      .getSetup()
      .then((result) => {
        if (!cancelled) setDismissed(result.dismissed);
      })
      .catch(() => {
        // The entry is a convenience: without the checklist state it stays out of the way
      });
    return () => {
      cancelled = true;
    };
  }, [api, allowed]);

  if (!allowed || (dismissed !== true && !restored)) return null;

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setSetupDismissed(false);
      setDismissed(false);
      setRestored(true);
    } catch (err) {
      setError(getErrorMessage(err, 'The setup checklist could not be shown.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 sm:mb-8" aria-labelledby="setup-checklist-heading">
      {error && <Toast kind="error">{error}</Toast>}
      <h2 id="setup-checklist-heading" className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Getting started</h2>
      <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
        {restored ? (
          <p className="text-sm text-gray-700">
            The setup checklist is back on your <Link to="/" className="font-semibold text-blue-600 hover:underline">dashboard</Link>.
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-500">The guided setup checklist is hidden.</p>
            <button
              type="button"
              onClick={restore}
              disabled={busy}
              className="mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-2 px-4 min-h-[44px] sm:min-h-0 rounded-md transition duration-200 disabled:opacity-50"
            >
              Show setup checklist
            </button>
          </>
        )}
      </div>
    </section>
  );
}
